import { FC, type JSX, ReactNode } from 'react';

import { Box, Card, Divider, Flex, Stack, Text } from '@mantine/core';

import {
    AllSubscriptionTypes,
    CreditSubscription,
    SubscriptionBillingCycle,
    SubscriptionData,
    SubscriptionFromSubscriptionType,
    SubscriptionType,
    Team,
} from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { usePrices } from 'hooks/use-prices';

import { PriceTagStacked } from './PriceTag';

export const PriceCalculation = <T extends SubscriptionType>({
    type,
    subscription,
    additionalSubscriptions = [],
    billingCycle,
    quantity,
    team,
}: {
    type: T;
    subscription: SubscriptionFromSubscriptionType<T>;
    additionalSubscriptions?: SubscriptionData[];
    billingCycle: SubscriptionBillingCycle;
    quantity: number;
    team: Team;
}) => {
    const { prices } = usePrices();

    const charges: number[] = [prices[subscription][billingCycle] * quantity];
    const existingLineItems: JSX.Element[] = [];

    for (const otherType of getOtherSubscriptionTypes(type)) {
        const subscriptionData = SubscriptionHelpers.getSubscriptionData(otherType, team.subscriptions);

        if (SubscriptionHelpers.isFreeSubscription(subscriptionData.subscription)) {
            continue;
        }

        const isBeingCancelled = SubscriptionHelpers.isGoingToBeCanceled(
            otherType,
            team.subscriptionUpdateNotice?.changes ?? [],
        );

        if (isBeingCancelled) {
            continue;
        }

        if (
            otherType === SubscriptionType.CREDIT &&
            additionalSubscriptions.some((sub) =>
                SubscriptionHelpers.isSubscriptionType(SubscriptionType.CREDIT, sub.subscription),
            )
        ) {
            continue;
        }

        existingLineItems.push(
            <SubscriptionCalculation
                key={otherType}
                type={otherType}
                subscription={subscriptionData.subscription}
                billingCycle={billingCycle}
                quantity={subscriptionData.quantity}
            />,
        );

        charges.push(prices[subscriptionData.subscription][billingCycle] * subscriptionData.quantity);
    }

    for (const additionalSubscription of additionalSubscriptions) {
        charges.push(prices[additionalSubscription.subscription][billingCycle] * additionalSubscription.quantity);
    }

    const total = charges.reduce((acc, charge) => acc + charge);

    return (
        <PriceCalculationCard total={total} billingCycle={billingCycle} existingLineItems={existingLineItems}>
            <SubscriptionCalculation
                type={type}
                subscription={subscription}
                billingCycle={billingCycle}
                quantity={quantity}
            />

            {additionalSubscriptions.map((subscriptionData) => {
                const { type, subscription } = SubscriptionHelpers.getSubscriptionAndType(
                    subscriptionData.subscription,
                );

                return (
                    <SubscriptionCalculation
                        key={subscriptionData.subscription}
                        type={type}
                        subscription={subscription}
                        billingCycle={billingCycle}
                        quantity={subscriptionData.quantity}
                    />
                );
            })}
        </PriceCalculationCard>
    );
};

const SubscriptionCalculation = <T extends SubscriptionType>({
    type,
    subscription,
    billingCycle,
    quantity,
}: {
    type: T;
    subscription: SubscriptionFromSubscriptionType<T>;
    billingCycle: SubscriptionBillingCycle;
    quantity: number;
}) => {
    if (type === SubscriptionType.CREDIT) {
        return (
            <CreditPackageSubscriptionCalculation
                subscription={subscription as CreditSubscription}
                billingCycle={billingCycle}
                quantity={quantity}
            />
        );
    }

    const config = SubscriptionConfigHelpers.getSubscriptionConfigByType(type)[subscription];

    return (
        <Flex justify="space-between">
            <Text span fw={600} fz={14}>
                {config.title} subscription
            </Text>
            <PriceTagStacked hidePeriod size="sm" subscription={subscription} billingCycle={billingCycle} />
        </Flex>
    );
};

const CreditPackageSubscriptionCalculation = ({
    subscription,
    billingCycle,
    quantity,
}: {
    subscription: CreditSubscription;
    billingCycle: SubscriptionBillingCycle;
    quantity: number;
}) => {
    const totalCredits = billingCycle === SubscriptionBillingCycle.YEARLY ? quantity * 12 : quantity;

    const lines = [`${IntlHelpers.formatNumber(quantity)} credits`];

    if (billingCycle === SubscriptionBillingCycle.YEARLY) {
        lines.push(`12 months = ${IntlHelpers.formatNumber(totalCredits)} credits`);
    }

    return (
        <Flex justify="space-between">
            <Text span fw={600} fz={14}>
                Credit Subscription
                <Text c="dimmed" fz="sm">
                    {lines.join(' × ')}
                </Text>
            </Text>

            <PriceTagStacked
                hidePeriod
                size="sm"
                subscription={subscription}
                billingCycle={billingCycle}
                multiplier={quantity}
            />
        </Flex>
    );
};

const PriceCalculationCard: FC<{
    total: number;
    billingCycle: SubscriptionBillingCycle;
    children: ReactNode;
    existingLineItems: JSX.Element[];
}> = ({ total, billingCycle, children, existingLineItems }) => (
    <Card withBorder padding="lg" radius="md" bg="gray.0">
        <Stack gap="xs">
            {existingLineItems.map((item, index) => (
                <Box key={index}>
                    {item}{' '}
                    <Text c="dimmed" fz="sm">
                        (Existing charge)
                    </Text>
                </Box>
            ))}

            {children}

            <Divider />

            <Flex justify="space-between">
                <Text span fw={600} fz="lg">
                    Total
                </Text>
                <Text span fw={600} fz="lg">
                    {`${IntlHelpers.formatPrice(total)} / ${billingCycle === SubscriptionBillingCycle.MONTHLY ? 'mo' : 'year'}`}
                </Text>
            </Flex>
        </Stack>
    </Card>
);

const getOtherSubscriptionTypes = (subscriptionType: SubscriptionType): SubscriptionType[] =>
    AllSubscriptionTypes.filter((type) => type !== subscriptionType);
